import * as T from 'three';
import { createSceneKit } from '@london-life/game/scene/kit';
import { softwareRenderer } from '@london-life/game/scene/gpu';
import { buildJet, JET_GROUND, JET_LENGTH, type Livery } from './jet';
import { CRUISE_HEIGHT, GATE, RUNWAY_LENGTH, flightProfile } from './profile';
import { CHOPPER_GROUND, buildChopper, chopperProfile } from './chopper';

// A flight, as Lagos Life's: the jet waits at the gate with its jet bridge and an advert board while players
// board, then rolls onto the runway, takes off over London, climbs through the clouds, cruises, comes down and
// lands at the other airport, timed by the server so it lands when the player arrives. Three ways to watch:
// outside beside the jet, in the cabin down the aisle, or from your seat with the seat-back moving map.

export type FlightView = 'outside' | 'cabin' | 'seat';
export type FlightOptions = {
  // a seat on the airline, the player's own jet (smaller, their name on it), or the VIP chopper
  mode?: 'air' | 'jet' | 'chopper';
  livery: Livery;
  // the server's times (ms): boarding ends (take-off), the jet lands
  takesOffAt: number;
  landsAt: number;
  from: { code: string; name: string };
  to: { code: string; name: string };
  night: boolean;
  dataSaver: boolean;
  view: FlightView;
  // a picture for the board by the gate (an airport advert, the airline's own until one is shown)
  advert: HTMLCanvasElement;
};
export type Flight = {
  ok: boolean;
  setView: (view: FlightView) => void;
  // the jet waits longer at the gate (or leaves sooner): the same flight, moved
  setTimes: (takesOffAt: number, landsAt: number) => void;
  // a player's ad on the board by the gate
  setAdvert: (art: HTMLCanvasElement) => void;
  dispose: () => void;
};

const SKY = { day: '#9cc8ec', night: '#0b1426' };
// where the London airports are on the seat-back map (the rest are spread around)
const MAP_SPOTS: Record<string, [number, number]> = { LHR: [92, 196], LGW: [262, 318], LCY: [404, 176] };

// the ground below: fields, roads and towns by day; streets and lights by night (one tile, repeated)
function paintGround(night: boolean, seed: number) {
  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d')!;
  let s = seed;
  const random = () => (s = (s * 48271) % 2147483647) / 2147483647;
  g.fillStyle = night ? '#121821' : '#7ea661';
  g.fillRect(0, 0, size, size);
  if (!night) {
    const fields = ['#86b06a', '#9bbb72', '#6f9a55', '#a7b47a', '#8fa463', '#b2a978'];
    for (let i = 0; i < 140; i += 1) {
      g.fillStyle = fields[i % fields.length]!;
      g.fillRect(random() * size, random() * size, 40 + random() * 120, 30 + random() * 100);
    }
  }
  // roads, and the towns along them
  for (let i = 0; i < 14; i += 1) {
    const vertical = i % 2 === 0;
    const at = random() * size;
    g.strokeStyle = night ? 'rgba(255,190,110,0.55)' : '#9ea3a6';
    g.lineWidth = night ? 2 : 4;
    g.beginPath();
    if (vertical) {
      g.moveTo(at, 0);
      g.bezierCurveTo(at + 60, size * 0.3, at - 60, size * 0.7, at, size);
    } else {
      g.moveTo(0, at);
      g.bezierCurveTo(size * 0.3, at - 50, size * 0.7, at + 50, size, at);
    }
    g.stroke();
  }
  for (let town = 0; town < 9; town += 1) {
    const cx = random() * size,
      cy = random() * size,
      r = 40 + random() * 90;
    for (let i = 0; i < (night ? 260 : 90); i += 1) {
      const a = random() * Math.PI * 2,
        d = Math.sqrt(random()) * r;
      const x = cx + Math.cos(a) * d,
        y = cy + Math.sin(a) * d;
      if (night) {
        g.fillStyle = random() < 0.8 ? 'rgba(255,214,140,0.9)' : 'rgba(255,255,235,0.95)';
        g.fillRect(x, y, 2, 2);
      } else {
        g.fillStyle = random() < 0.5 ? '#b7b2a8' : '#a4553f';
        g.fillRect(x, y, 6, 5);
      }
    }
  }
  const texture = new T.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = T.RepeatWrapping;
  texture.colorSpace = T.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

// an airport on the ground: the runway with its markings and lights, the taxiway, the apron, the terminal with
// its name, the control tower
function buildAirport(kit: ReturnType<typeof createSceneKit>, name: string, colour: string, night: boolean) {
  const group = new T.Group();
  const runway = RUNWAY_LENGTH;
  kit.box(runway + 80, 0.1, 70, '#8d949a', runway / 2 - 200, 0.05, 0, group);
  kit.box(runway, 0.12, 45, '#383f47', runway / 2 - 200, 0.1, 0, group);
  for (let d = -160; d < runway - 260; d += 50) kit.box(28, 0.02, 1, '#f4f4ea', d, 0.18, 0, group);
  for (const side of [-1, 1]) kit.box(runway - 10, 0.02, 0.8, '#f4f4ea', runway / 2 - 200, 0.18, side * 21, group);
  for (const end of [-190, runway - 210])
    for (let k = -18; k <= 18; k += 4) kit.box(30, 0.02, 1.8, '#f4f4ea', end, 0.18, k, group);
  for (let d = -195; d < runway - 200; d += 60)
    for (const side of [-1, 1]) {
      if (night) kit.glow(0.6, '#fff1c2', d, 0.6, side * 24, group);
      else kit.box(0.8, 0.6, 0.8, '#e9e2c4', d, 0.4, side * 24, group);
    }
  // the apron and the taxiway off it, the terminal behind with its glass and its name
  kit.box(420, 0.1, 60, '#bfc4c8', GATE.x + 30, 0.06, GATE.z - 14, group);
  kit.box(240, 0.11, 22, '#4e5760', GATE.x + 150, 0.08, GATE.z / 2, group);
  kit.box(320, 18, 34, '#e3e8ec', GATE.x + 20, 9, GATE.z - 58, group);
  kit.box(321, 7, 34.5, '#7fb3cf', GATE.x + 20, 10, GATE.z - 58, group);
  kit.box(326, 1.6, 38, '#b8c2ca', GATE.x + 20, 18.8, GATE.z - 58, group);
  kit.sign(name.toUpperCase(), GATE.x + 20, 12, GATE.z - 40.8, group, 56, '#ffffff', colour);
  // the stands' yellow lines
  for (let k = -3; k <= 3; k += 1) kit.box(60, 0.02, 0.7, '#e8c547', GATE.x + k * 70, 0.14, GATE.z, group);
  // the tower
  kit.cylinder(4, 50, '#e7eaed', GATE.x + 230, 25, GATE.z - 70, group, 3.2, 14);
  kit.cylinder(9, 7, '#5d9cc0', GATE.x + 230, 53, GATE.z - 70, group, 10, 14);
  kit.cylinder(10, 1.5, '#e7eaed', GATE.x + 230, 57.5, GATE.z - 70, group, 10, 14);
  return group;
}

// the seat-back screen: the route from one airport to the other, the jet along it, the time left
function drawMovingMap(canvas: HTMLCanvasElement, from: string, to: string, progress: number, title: string) {
  const g = canvas.getContext('2d')!;
  const { width: w, height: h } = canvas;
  g.fillStyle = '#0f2340';
  g.fillRect(0, 0, w, h);
  g.fillStyle = '#2a5a3a';
  g.beginPath();
  g.ellipse(w * 0.5, h * 0.58, w * 0.44, h * 0.34, -0.25, 0, Math.PI * 2);
  g.fill();
  // the Thames across the middle
  g.strokeStyle = '#3b7fc4';
  g.lineWidth = 7;
  g.beginPath();
  g.moveTo(w * 0.08, h * 0.5);
  g.bezierCurveTo(w * 0.35, h * 0.42, w * 0.55, h * 0.62, w * 0.95, h * 0.44);
  g.stroke();
  const spot = (code: string, i: number): [number, number] => MAP_SPOTS[code] ?? [w * (0.25 + 0.5 * i), h * 0.55];
  const a = spot(from, 0),
    b = spot(to, 1);
  const bend: [number, number] = [(a[0] + b[0]) / 2, Math.min(a[1], b[1]) - h * 0.18];
  const along = (t: number): [number, number] => [
    (1 - t) ** 2 * a[0] + 2 * (1 - t) * t * bend[0] + t * t * b[0],
    (1 - t) ** 2 * a[1] + 2 * (1 - t) * t * bend[1] + t * t * b[1],
  ];
  g.setLineDash([10, 9]);
  g.strokeStyle = 'rgba(255,255,255,0.85)';
  g.lineWidth = 4;
  g.beginPath();
  g.moveTo(...a);
  g.quadraticCurveTo(bend[0], bend[1], b[0], b[1]);
  g.stroke();
  g.setLineDash([]);
  g.fillStyle = '#ffffff';
  g.font = '800 30px Arial, sans-serif';
  g.textAlign = 'center';
  g.fillText(from, a[0], a[1] + 40);
  g.fillText(to, b[0], b[1] - 18);
  // the jet on its way
  const [px, py] = along(progress);
  const [qx, qy] = along(Math.min(1, progress + 0.01));
  g.save();
  g.translate(px, py);
  g.rotate(Math.atan2(qy - py, qx - px));
  g.fillStyle = '#ffffff';
  g.beginPath();
  g.moveTo(16, 0);
  g.lineTo(-12, -11);
  g.lineTo(-6, 0);
  g.lineTo(-12, 11);
  g.closePath();
  g.fill();
  g.restore();
  g.textAlign = 'left';
  g.font = '700 26px Arial, sans-serif';
  g.fillText(title, 18, 38);
}

// the cabin, inside the jet: rows of seats in the airline's colour (some taken), the overhead lockers, the
// windows, the lights down the ceiling, the seat-back screen in front of the player's seat
function buildCabin(livery: Livery, night: boolean) {
  const group = new T.Group();
  const disposables: { dispose: () => void }[] = [];
  const keep = <D extends { dispose: () => void }>(item: D) => (disposables.push(item), item);
  const material = (color: string, extra: Partial<T.MeshStandardMaterialParameters> = {}) =>
    keep(new T.MeshStandardMaterial({ color, roughness: 0.75, ...extra }));
  const shell = new T.Mesh(
    keep(new T.CylinderGeometry(1.95, 1.95, 26, 28, 1, true)),
    material('#eef0f2', { side: T.BackSide, emissive: '#d5d9de', emissiveIntensity: 0.55 }),
  );
  shell.rotation.z = Math.PI / 2;
  shell.position.x = 2;
  group.add(shell);
  for (const end of [-11, 15]) {
    const wall = new T.Mesh(keep(new T.CircleGeometry(1.95, 28)), material('#d9dde1', { side: T.DoubleSide }));
    wall.rotation.y = Math.PI / 2;
    wall.position.x = end;
    group.add(wall);
  }
  const floor = new T.Mesh(keep(new T.BoxGeometry(26, 0.1, 3.6)), material('#3b3f63'));
  floor.position.set(2, -0.95, 0);
  group.add(floor);
  const rows: number[] = [];
  for (let x = -9.5; x < 13.5; x += 0.82) rows.push(x);
  const across = [-1.35, -0.85, -0.35, 0.35, 0.85, 1.35];
  // the player's seat (left, by the aisle); the screen is in the seat back ahead
  const row = Math.floor(rows.length * 0.45);
  const mine = { x: rows[row]!, z: -0.35 };
  const count = rows.length * across.length;
  const base = new T.InstancedMesh(keep(new T.BoxGeometry(0.5, 0.14, 0.46)), material(livery.tail), count);
  const back = new T.InstancedMesh(keep(new T.BoxGeometry(0.12, 0.8, 0.46)), material(livery.tail), count);
  const cover = new T.InstancedMesh(keep(new T.BoxGeometry(0.13, 0.2, 0.36)), material('#f2f2ee'), count);
  const heads = new T.InstancedMesh(keep(new T.SphereGeometry(0.13, 10, 8)), material('#ffffff'), count);
  const skins = ['#f1c7a6', '#d9a17f', '#a8704f', '#7a4b31', '#4f2f1f'].map((c) => new T.Color(c));
  const place = new T.Object3D();
  let n = 0,
    taken = 0;
  for (const x of rows)
    for (const z of across) {
      place.position.set(x, -0.5, z);
      place.updateMatrix();
      base.setMatrixAt(n, place.matrix);
      place.position.set(x - 0.22, -0.05, z);
      place.updateMatrix();
      back.setMatrixAt(n, place.matrix);
      place.position.set(x - 0.22, 0.3, z);
      place.updateMatrix();
      cover.setMatrixAt(n, place.matrix);
      // some seats taken: a head over the seat back
      if ((n * 7919) % 10 < 6 && !(x === mine.x && z === mine.z)) {
        place.position.set(x - 0.1, 0.33, z);
        place.updateMatrix();
        heads.setMatrixAt(taken, place.matrix);
        heads.setColorAt(taken, skins[(n * 31) % skins.length]!);
        taken += 1;
      }
      n += 1;
    }
  heads.count = taken;
  group.add(base, back, cover, heads);
  // the overhead lockers and the lights down the ceiling
  for (const side of [-1, 1]) {
    const bins = new T.Mesh(keep(new T.BoxGeometry(24, 0.5, 0.62)), material('#f6f7f8'));
    bins.position.set(2, 1.15, side * 1.42);
    bins.rotation.x = side * 0.25;
    group.add(bins);
  }
  const light = new T.Mesh(
    keep(new T.BoxGeometry(24, 0.04, 0.3)),
    keep(new T.MeshBasicMaterial({ color: night ? '#cfd8ff' : '#fffaf0' })),
  );
  light.position.set(2, 1.86, 0);
  group.add(light);
  // the windows, showing the sky
  const pane = new T.InstancedMesh(
    keep(new T.PlaneGeometry(0.26, 0.36)),
    keep(new T.MeshBasicMaterial({ color: night ? '#1d2b4f' : '#cfe6fa', side: T.DoubleSide })),
    rows.length * 2,
  );
  let p = 0;
  for (const x of rows)
    for (const side of [-1, 1]) {
      place.position.set(x + 0.1, 0.42, side * 1.83);
      place.rotation.set(0, 0, 0);
      place.updateMatrix();
      pane.setMatrixAt(p, place.matrix);
      p += 1;
    }
  group.add(pane);
  const screenCanvas = document.createElement('canvas');
  screenCanvas.width = 512;
  screenCanvas.height = 384;
  const screenTexture = keep(new T.CanvasTexture(screenCanvas));
  screenTexture.colorSpace = T.SRGBColorSpace;
  const screen = new T.Mesh(
    keep(new T.PlaneGeometry(0.3, 0.225)),
    keep(new T.MeshBasicMaterial({ map: screenTexture })),
  );
  const ahead = rows[row + 1]! - 0.22 - 0.065;
  screen.position.set(ahead, 0.05, mine.z);
  screen.rotation.y = -Math.PI / 2;
  group.add(screen);
  return {
    group,
    // the views: down the aisle from the back, and from the player's seat
    aisle: { eye: new T.Vector3(-10, 0.75, 0), look: new T.Vector3(12, 0.35, 0) },
    seat: { eye: new T.Vector3(mine.x - 0.3, 0.36, mine.z), look: new T.Vector3(ahead, 0.08, mine.z) },
    screenCanvas,
    screenTexture,
    dispose: () => disposables.forEach((item) => item.dispose()),
  };
}

export function createFlight(element: HTMLElement, options: FlightOptions): Flight {
  let renderer: T.WebGLRenderer;
  try {
    renderer = new T.WebGLRenderer({ antialias: !options.dataSaver, powerPreference: 'low-power' });
  } catch {
    return { ok: false, setView: () => {}, setTimes: () => {}, setAdvert: () => {}, dispose: () => {} };
  }
  const slow = softwareRenderer(renderer);
  renderer.setPixelRatio(slow ? 0.6 : options.dataSaver ? 1 : Math.min(devicePixelRatio, 1.5));
  renderer.toneMapping = T.ACESFilmicToneMapping;
  const canvas = renderer.domElement;
  canvas.setAttribute('aria-hidden', 'true');
  element.appendChild(canvas);

  const kit = createSceneKit();
  const scene = new T.Scene();
  const sky = options.night ? SKY.night : SKY.day;
  scene.background = new T.Color(sky);
  scene.fog = new T.Fog(sky, 300, slow ? 1400 : 2600);
  scene.add(
    options.night ? new T.HemisphereLight('#8f9fcf', '#1a1f28', 1.0) : new T.HemisphereLight('#fff6e6', '#6f7f6a', 1.4),
  );
  const sun = new T.DirectionalLight(options.night ? '#aebcff' : '#ffe9c7', options.night ? 0.6 : 2.4);
  sun.position.set(-200, 400, 300);
  scene.add(sun);
  const camera = new T.PerspectiveCamera(50, 1, 0.05, 6000);

  const seconds = Math.max(10, (options.landsAt - options.takesOffAt) / 1000);
  const times = { off: options.takesOffAt, lands: options.landsAt };
  const mode = options.mode ?? 'air';
  const chopperRide = mode === 'chopper';
  const path = chopperRide ? chopperProfile(seconds) : flightProfile(seconds);
  // a private jet is smaller than the airline's
  const size = mode === 'jet' ? 0.6 : 1;
  const middle = (GATE.x + path.stopsAt) / 2;

  // the ground under the whole way, the two airports, the river on the way
  const groundTexture = paintGround(options.night, 4242);
  const groundSize = path.stopsAt - GATE.x + 12000;
  groundTexture.repeat.set(groundSize / 700, groundSize / 700);
  const ground = new T.Mesh(
    new T.PlaneGeometry(groundSize, groundSize),
    new T.MeshStandardMaterial({ map: groundTexture, roughness: 1 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(middle, -0.02, 0);
  scene.add(ground);
  const river = new T.Mesh(
    new T.PlaneGeometry(120, groundSize),
    new T.MeshStandardMaterial({ color: options.night ? '#0d2236' : '#4f8fbf', roughness: 0.4 }),
  );
  river.rotation.set(-Math.PI / 2, 0, 0.5);
  river.position.set(middle, 0.01, 0);
  scene.add(river);
  scene.add(buildAirport(kit, options.from.name, options.livery.tail, options.night));
  if (chopperRide) {
    // the helipad it lifts off from, and the one at the door it lands on, among the buildings there
    for (const at of [GATE.x, path.stopsAt]) {
      kit.cylinder(9, 0.3, '#e8c547', at, 0.15, GATE.z, scene, 9, 28);
      kit.cylinder(8, 0.32, '#3e464f', at, 0.2, GATE.z, scene, 8, 28);
      kit.box(1.2, 0.05, 7, '#f4f4ea', at - 2, 0.38, GATE.z, scene);
      kit.box(1.2, 0.05, 7, '#f4f4ea', at + 2, 0.38, GATE.z, scene);
      kit.box(5, 0.05, 1.2, '#f4f4ea', at, 0.38, GATE.z, scene);
    }
    let seed = 9;
    const next = () => (seed = (seed * 48271) % 2147483647) / 2147483647;
    for (let i = 0; i < 26; i += 1) {
      const a = next() * Math.PI * 2,
        r = 26 + next() * 70;
      const h = 8 + next() * 40;
      kit.box(
        10 + next() * 10,
        h,
        10 + next() * 10,
        ['#c8795b', '#d9dde2', '#b5553f', '#e4cdb0'][i % 4]!,
        path.stopsAt + Math.cos(a) * r,
        h / 2,
        GATE.z + Math.sin(a) * r,
        scene,
      );
    }
    const name = kit.sign(
      options.to.name.toUpperCase(),
      path.stopsAt,
      6,
      GATE.z - 26,
      scene,
      36,
      '#ffffff',
      options.livery.tail,
    );
    void name;
  } else {
    const arrival = buildAirport(kit, options.to.name, options.livery.tail, options.night);
    // laid so the jet touches down a little way along its runway and stops by the terminal
    arrival.position.x = path.landsAt - GATE.x - 120;
    scene.add(arrival);
  }
  // the board by the gate
  const board = new T.Group();
  board.position.set(GATE.x + 70, 0, GATE.z - 22);
  board.rotation.y = -0.35;
  kit.box(29, 15, 1, '#2b3542', 0, 16, 0, board);
  const screen = kit.picture(options.advert, 28, 14, 0, 16, 0.6, board, 0, 0);
  let shown: T.CanvasTexture | null = null;
  for (const side of [-1, 1]) kit.box(0.8, 9, 0.8, '#4e5a66', side * 10, 4.5, 0, board);
  scene.add(board);

  // the clouds: a layer the climb goes through, more above it
  const clouds = new T.Group();
  const cloudMaterial = new T.MeshStandardMaterial({
    color: options.night ? '#4a5677' : '#ffffff',
    transparent: true,
    opacity: options.night ? 0.55 : 0.88,
    roughness: 1,
  });
  const puff = new T.SphereGeometry(1, 10, 8);
  let s = 77;
  const random = () => (s = (s * 48271) % 2147483647) / 2147483647;
  for (let i = 0; i < (slow ? 50 : 110); i += 1) {
    const cloud = new T.Group();
    cloud.position.set(
      GATE.x + 400 + random() * Math.max(400, path.stopsAt - GATE.x - 900),
      CRUISE_HEIGHT * (0.45 + random() * 0.3),
      (random() - 0.5) * 900,
    );
    for (let k = 0; k < 4; k += 1) {
      const part = new T.Mesh(puff, cloudMaterial);
      part.scale.set(30 + random() * 30, 8 + random() * 7, 22 + random() * 20);
      part.position.set((random() - 0.5) * 50, random() * 4, (random() - 0.5) * 30);
      cloud.add(part);
    }
    clouds.add(cloud);
  }
  scene.add(clouds);
  // stars at night
  let stars: T.Points | null = null;
  if (options.night) {
    const points: number[] = [];
    for (let i = 0; i < 900; i += 1) {
      const a = random() * Math.PI * 2,
        up = 0.15 + random() * 0.85;
      points.push(Math.cos(a) * 4000 * (1 - up * 0.5), 600 + up * 2200, Math.sin(a) * 4000 * (1 - up * 0.5));
    }
    const geometry = new T.BufferGeometry();
    geometry.setAttribute('position', new T.Float32BufferAttribute(points, 3));
    stars = new T.Points(
      geometry,
      new T.PointsMaterial({ color: '#ffffff', size: 2, sizeAttenuation: false, fog: false }),
    );
    scene.add(stars);
  }

  // the jet (or the chopper), the jet bridge at the gate, the cabin inside
  const jet = chopperRide ? null : buildJet(options.livery);
  const chopper = chopperRide ? buildChopper(options.livery) : null;
  const holder = new T.Group();
  const model = new T.Group();
  model.scale.setScalar(size);
  holder.add(model);
  if (jet) {
    model.add(jet.group);
    jet.group.position.y = JET_GROUND;
  }
  if (chopper) {
    model.add(chopper.group);
    chopper.group.position.y = CHOPPER_GROUND;
  }
  scene.add(holder);
  const cabin = buildCabin(options.livery, options.night);
  cabin.group.position.y = JET_GROUND;
  if (jet) model.add(cabin.group);
  const bridge = new T.Group();
  const door = new T.Vector3(GATE.x + JET_LENGTH / 2 - 6.8, JET_GROUND + 0.4, GATE.z - 2.3);
  const terminal = new T.Vector3(GATE.x + 8, JET_GROUND + 0.4, GATE.z - 41);
  const reach = door.clone().sub(terminal);
  const tunnel = kit.box(reach.length(), 3, 3, '#aeb6bd', 0, 0, 0, bridge);
  tunnel.rotation.y = -Math.atan2(reach.z, reach.x);
  bridge.position.copy(terminal.clone().add(door).multiplyScalar(0.5));
  kit.box(4, 3.6, 4, '#8f989f', door.x - bridge.position.x, 0, door.z - bridge.position.z + 1, bridge);
  kit.cylinder(0.5, JET_GROUND, '#5d666e', 0, -JET_GROUND / 2 - 0.2, 0, bridge, 0.5, 8);
  // the airline's jets board through a bridge; a private jet and the chopper go from the apron
  if (mode === 'air') scene.add(bridge);

  let view: FlightView = options.view;
  const eye = new T.Vector3();
  const look = new T.Vector3();
  const world = new T.Vector3();
  const showView = () => {
    cabin.group.visible = view !== 'outside' && !chopperRide;
    camera.fov = view === 'seat' ? 56 : view === 'cabin' ? 62 : 50;
    camera.near = view === 'outside' ? 0.5 : 0.05;
    camera.updateProjectionMatrix();
  };
  showView();

  const resize = () => {
    const { clientWidth: w, clientHeight: h } = element;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  resize();
  const observer = new ResizeObserver(resize);
  observer.observe(element);

  let frame = 0;
  let lastScreen = 0;
  let snapped = false;
  const loop = (now: number) => {
    frame = requestAnimationFrame(loop);
    const t = (Date.now() - times.off) / 1000;
    const point = path.pointAt(Math.max(0, t));
    holder.position.set(point.x, point.y, point.z);
    holder.rotation.set(0, point.heading, point.pitch, 'YXZ');
    jet?.setGear(point.gearDown);
    jet?.update(now / 1000);
    chopper?.update(now / 1000);
    // the bridge pulls back as the jet leaves
    bridge.visible = t < 0.5;
    if (view === 'outside' || chopperRide) {
      // beside the jet on its right, a little ahead and above (as Lagos Life's), far enough out that all of it
      // is on the screen however narrow, further out in the air
      const across = Math.tan(T.MathUtils.degToRad(camera.fov / 2)) * Math.max(0.3, camera.aspect);
      // the camera keeps the same framing for the smaller private jet and the chopper
      const reach = chopperRide ? 0.42 : size;
      const out = Math.max(52, 25 / across) * reach * (point.y > 20 ? 1.15 : 1);
      eye.set(point.x + 14 * reach, point.y + (JET_GROUND + (point.y > 20 ? 10 : 8)) * reach, point.z + out);
      look.set(point.x + 4 * reach, point.y + JET_GROUND * reach, point.z);
      if (!snapped) camera.position.copy(eye);
      else camera.position.lerp(eye, 0.12);
      snapped = true;
      camera.lookAt(look);
    } else {
      const spot = view === 'cabin' ? cabin.aisle : cabin.seat;
      holder.updateMatrixWorld(true);
      camera.position.copy(cabin.group.localToWorld(world.copy(spot.eye)));
      camera.lookAt(cabin.group.localToWorld(world.copy(spot.look)));
      snapped = false;
    }
    // the seat-back screen, a few times a second
    if (view === 'seat' && now - lastScreen > 400) {
      lastScreen = now;
      const left = Math.max(0, Math.round(((times.lands - Date.now()) / 1000) * 2));
      const title =
        t < 0 ? `Boarding · ${Math.round(seconds * 2)} min flight` : left > 0 ? `${left} min to landing` : 'Landed';
      drawMovingMap(cabin.screenCanvas, options.from.code, options.to.code, point.progress, title);
      cabin.screenTexture.needsUpdate = true;
    }
    if (stars) stars.position.set(camera.position.x, 0, camera.position.z);
    renderer.render(scene, camera);
  };
  frame = requestAnimationFrame(loop);

  return {
    ok: true,
    setAdvert: (art) => {
      shown?.dispose();
      shown = new T.CanvasTexture(art);
      shown.colorSpace = T.SRGBColorSpace;
      const material = screen.material as T.MeshBasicMaterial;
      material.map = shown;
      material.needsUpdate = true;
    },
    setTimes: (takesOffAt, landsAt) => {
      times.off = takesOffAt;
      times.lands = landsAt;
    },
    setView: (next) => {
      view = next;
      lastScreen = 0;
      showView();
    },
    dispose: () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      jet?.dispose();
      chopper?.dispose();
      shown?.dispose();
      cabin.dispose();
      groundTexture.dispose();
      ground.geometry.dispose();
      (ground.material as T.Material).dispose();
      river.geometry.dispose();
      (river.material as T.Material).dispose();
      puff.dispose();
      cloudMaterial.dispose();
      stars?.geometry.dispose();
      (stars?.material as T.Material | undefined)?.dispose();
      kit.dispose();
      renderer.dispose();
      canvas.remove();
    },
  };
}
